/**
 * Routes for the app list and for every section's overview.
 *
 * The overview routes are what make a group header in the sidebar somewhere
 * you can actually go. Before this, `/catalog` and `/inventory` were labels
 * with nothing behind them; now each is the section's own page — an overview
 * when the company has the app, and the app's intro with an Install button
 * when it does not.
 */
import { createRoute, useNavigate, type AnyRoute } from "@tanstack/react-router";

import { APPS, CORE, appOverviewPath } from "@/lib/modules";

import { AppOverviewPage } from "./pages/app-overview-page";
import { AppsPage } from "./pages/apps-page";

/**
 * The screens each section's overview offers as a way in. Kept here rather
 * than derived from the nav tree so an overview can lead with the two or three
 * screens somebody actually starts from, instead of repeating the whole menu.
 */
export const SECTION_ENTRIES: Record<string, readonly { path: string; labelKey: string }[]> = {
  core: [
    { path: "/admin/users", labelKey: "identity.nav.users" },
    { path: "/admin/roles", labelKey: "identity.nav.roles" },
    { path: "/admin/branches", labelKey: "platform.nav.branches" },
    { path: "/admin/audit-log", labelKey: "nav.auditLog" },
  ],
  catalog: [
    { path: "/catalog/products", labelKey: "nav.catalogProducts" },
    { path: "/catalog/price-lists", labelKey: "nav.catalogPriceLists" },
    { path: "/catalog/uoms", labelKey: "nav.catalogUoms" },
  ],
  partners: [{ path: "/partners", labelKey: "partners.nav.parties" }],
  accounting: [
    { path: "/ledger/accounts", labelKey: "ledger.nav.accounts" },
    { path: "/ledger/journals", labelKey: "ledger.nav.journals" },
    { path: "/ledger/trial-balance", labelKey: "ledger.nav.trialBalance" },
    { path: "/finance/statements", labelKey: "finance.nav.statements" },
    { path: "/finance/period-close", labelKey: "finance.nav.periodClose" },
  ],
  inventory: [
    { path: "/inventory/stock", labelKey: "inventory.nav.stock" },
    { path: "/inventory/movements", labelKey: "inventory.nav.movements" },
    { path: "/inventory/counts", labelKey: "inventory.nav.counts" },
  ],
  tax: [
    { path: "/finance/tax-codes", labelKey: "finance.nav.taxCodes" },
    { path: "/finance/proofs", labelKey: "finance.nav.proofs" },
  ],
  receivables: [
    { path: "/finance/customer-invoices", labelKey: "finance.nav.customerInvoices" },
    { path: "/finance/customer-receipts/new", labelKey: "finance.nav.customerReceipts" },
    { path: "/finance/collections", labelKey: "finance.nav.collections" },
  ],
  payables: [
    { path: "/finance/supplier-invoices", labelKey: "finance.nav.supplierInvoices" },
    { path: "/finance/payment-runs", labelKey: "finance.nav.paymentRuns" },
  ],
  banking: [
    { path: "/finance/bank-accounts", labelKey: "finance.nav.bankAccounts" },
    { path: "/finance/bank-statements", labelKey: "finance.nav.bankStatements" },
    { path: "/finance/bank-reconciliation", labelKey: "finance.nav.reconciliation" },
  ],
  fixed_assets: [
    { path: "/finance/fixed-assets", labelKey: "finance.nav.fixedAssets" },
    { path: "/finance/asset-classes", labelKey: "finance.nav.assetClasses" },
    { path: "/finance/depreciation-runs", labelKey: "finance.nav.depreciationRuns" },
  ],
  purchasing: [
    { path: "/purchasing/purchase-orders", labelKey: "purchasing.nav.purchaseOrders" },
    { path: "/purchasing/grns", labelKey: "purchasing.nav.goodsReceipts" },
  ],
  sales: [
    { path: "/sales/quotes", labelKey: "sales.nav.quotes" },
    { path: "/sales/orders", labelKey: "sales.nav.orders" },
    { path: "/sales/fulfilments", labelKey: "sales.nav.fulfilments" },
  ],
  channels: [
    { path: "/channels/list", labelKey: "channels.nav.channels" },
    { path: "/channels/orders", labelKey: "channels.nav.orders" },
  ],
};

function AppsRoute() {
  const navigate = useNavigate();
  return <AppsPage onOpenApp={(path) => void navigate({ to: path as never })} />;
}

export function appsRoutes(parent: AnyRoute): AnyRoute[] {
  const overviews = APPS.map((app) =>
    createRoute({
      getParentRoute: () => parent,
      path: appOverviewPath(app.code).replace(/^\//, ""),
      component: function AppOverviewRoute() {
        const navigate = useNavigate();
        return (
          <AppOverviewPage
            code={app.code}
            entries={SECTION_ENTRIES[app.code] ?? []}
            onNavigate={(path) => void navigate({ to: path as never })}
            onOpenApps={() => void navigate({ to: "/admin/apps" as never })}
          />
        );
      },
    }),
  );
  return [
    createRoute({
      getParentRoute: () => parent,
      path: "admin/apps",
      component: AppsRoute,
    }),
    ...overviews,
  ] as AnyRoute[];
}

export { CORE };
